import { useCustom, useNavigation } from '@refinedev/core';
import { Row, Col, Card, Statistic, Typography, Spin, List, Tag, Avatar } from 'antd';
import {
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ShopOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { isBusOnlyMode, isTaxiOnlyMode } from '../contexts/ServiceContext';

const { Title, Text } = Typography;

interface BusDashboardStats {
  companies: {
    total: number;
    active: number;
  };
  trips: {
    total: number;
    scheduled: number;
    today: number;
  };
  bookings: {
    total: number;
    today: number;
  };
  revenue: {
    total: number;
    today: number;
  };
}

interface AuditLog {
  id: number;
  action: string;
  entity: string;
  entityId: number | null;
  createdAt: string;
  staff: {
    id: number;
    username: string;
    fullName: string | null;
  };
}

const actionLabels: Record<string, string> = {
  'bus.company.create': 'Kompaniya yaratildi',
  'bus.company.update': 'Kompaniya tahrirlandi',
  'bus.trip.create': 'Reys yaratildi',
  'bus.trip.update': 'Reys tahrirlandi',
  'bus.trip.cancel': 'Reys bekor qilindi',
  'bus.booking.create': 'Buyurtma yaratildi',
  'bus.booking.confirm': 'Buyurtma tasdiqlandi',
  'bus.booking.cancel': 'Buyurtma bekor qilindi',
  'bus.station.create': 'Avtovokzal yaratildi',
  'bus.layout.create': 'Sxema yaratildi',
  'staff.create': 'Xodim yaratildi',
  'staff.update': 'Xodim tahrirlandi',
  'ticket.reply': 'Murojaatga javob berildi',
  'ticket.resolve': 'Murojaat hal qilindi',
  'faq.create': 'FAQ yaratildi',
  'faq.update': 'FAQ tahrirlandi',
  'promo.create': 'Promo kod yaratildi',
  'settings.update': 'Sozlamalar tahrirlandi',
};

const actionColors: Record<string, string> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  confirm: 'green',
  cancel: 'orange',
  reply: 'blue',
  resolve: 'green',
};

export const Dashboard = () => {
  const { push } = useNavigation();
  const busOnlyMode = isBusOnlyMode();
  const taxiOnlyMode = isTaxiOnlyMode();

  const { data, isLoading } = useCustom<BusDashboardStats>({
    url: '/admin/bus/stats',
    method: 'get',
    queryOptions: {
      enabled: busOnlyMode,
    },
  });

  const { data: activityData, isLoading: activityLoading } = useCustom<{ data: AuditLog[] }>({
    url: '/admin/audit-logs',
    method: 'get',
    config: {
      query: {
        limit: 10,
      },
    },
  });

  const stats = data?.data;
  const recentActivity = activityData?.data?.data || [];

  if (busOnlyMode && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const getActionColor = (action: string) => {
    const parts = action.split('.');
    const actionType = parts[parts.length - 1];
    return actionColors[actionType] || 'default';
  };

  const quickActions = busOnlyMode
    ? [
        {
          title: 'Yangi reys',
          icon: <CalendarOutlined />,
          onClick: () => push('/bus/trips/create'),
          color: '#9B59B6',
        },
        {
          title: 'Kompaniyalar',
          icon: <ShopOutlined />,
          onClick: () => push('/bus/companies'),
          color: '#3498DB',
        },
        {
          title: 'Buyurtmalar',
          icon: <CheckCircleOutlined />,
          onClick: () => push('/bus/bookings'),
          color: '#27AE60',
        },
        {
          title: 'Sozlamalar',
          icon: <SettingOutlined />,
          onClick: () => push('/bus/settings'),
          color: '#7F8C8D',
        },
      ]
    : [
        {
          title: 'Haydovchilar',
          icon: <CarOutlined />,
          onClick: () => push('/drivers'),
          color: '#1ABC9C',
        },
        {
          title: 'Hujjatlar tekshirish',
          icon: <CheckCircleOutlined />,
          onClick: () => push('/documents'),
          color: '#E67E22',
        },
        {
          title: 'Sayohatlar',
          icon: <EnvironmentOutlined />,
          onClick: () => push('/trips'),
          color: '#3498DB',
        },
        {
          title: "Foydalanuvchilar",
          icon: <UserOutlined />,
          onClick: () => push('/users'),
          color: '#9B59B6',
        },
      ];

  return (
    <div className="p-6">
      <Title level={4} style={{ marginBottom: 24 }}>
        {busOnlyMode ? 'Avtobuslar boshqaruvi' : taxiOnlyMode ? 'Taxi & Pochta boshqaruvi' : 'Bosh sahifa'}
      </Title>

      {/* Quick Actions */}
      <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
        Tezkor amallar
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {quickActions.map((action, index) => (
          <Col xs={12} sm={8} md={6} key={index}>
            <Card
              hoverable
              onClick={action.onClick}
              style={{ textAlign: 'center', cursor: 'pointer' }}
              styles={{ body: { padding: 16 } }}
            >
              <div
                style={{
                  fontSize: 24,
                  color: action.color,
                  marginBottom: 8,
                }}
              >
                {action.icon}
              </div>
              <Text style={{ fontSize: 12 }}>{action.title}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      {busOnlyMode ? (
        <>
          {/* Bus Companies Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            Kompaniyalar
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Jami kompaniyalar"
                  value={stats?.companies?.total || 0}
                  prefix={<ShopOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Faol kompaniyalar"
                  value={stats?.companies?.active || 0}
                  prefix={<CheckCircleOutlined style={{ color: '#27AE60' }} />}
                  valueStyle={{ color: '#27AE60' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Bus Trips Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            Reyslar
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Jami reyslar"
                  value={stats?.trips?.total || 0}
                  prefix={<CalendarOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Bugungi reyslar"
                  value={stats?.trips?.today || 0}
                  prefix={<CalendarOutlined style={{ color: '#3498DB' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card>
                <Statistic
                  title="Rejalashtirilgan"
                  value={stats?.trips?.scheduled || 0}
                  prefix={<ClockCircleOutlined style={{ color: '#F39C12' }} />}
                  valueStyle={{ color: '#F39C12' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Bus Bookings Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            Buyurtmalar
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Jami buyurtmalar"
                  value={stats?.bookings?.total || 0}
                  prefix={<UserOutlined style={{ color: '#9B59B6' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Bugungi buyurtmalar"
                  value={stats?.bookings?.today || 0}
                  prefix={<UserOutlined style={{ color: '#3498DB' }} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Revenue Section */}
          <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
            Daromad
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Jami daromad"
                  value={stats?.revenue?.total || 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  valueStyle={{ color: '#9B59B6' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Bugungi daromad"
                  value={stats?.revenue?.today || 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  valueStyle={{ color: '#3498DB' }}
                />
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      {/* Recent Activity */}
      <Title level={5} style={{ marginBottom: 16, color: '#7F8C8D' }}>
        So'nggi faoliyat
      </Title>
      <Card styles={{ body: { padding: 0 } }}>
        <List
          loading={activityLoading}
          dataSource={recentActivity}
          size="small"
          renderItem={(item: AuditLog) => (
            <List.Item style={{ padding: '12px 16px' }}>
              <List.Item.Meta
                avatar={
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#9B59B6' }}
                  />
                }
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {item.staff?.fullName || item.staff?.username}
                    </Text>
                    <Tag color={getActionColor(item.action)} style={{ margin: 0 }}>
                      {actionLabels[item.action] || item.action}
                    </Tag>
                  </div>
                }
                description={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(item.createdAt).toLocaleString('uz-UZ')}
                  </Text>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: 'Faoliyat topilmadi' }}
        />
      </Card>
    </div>
  );
};
